import { findDates } from "./chartDates";
import { ChartData } from "./types";
import { useZoom } from "../user/hooks/useZoom";
import { TokenDataNew } from "../user/types";
import { setTokens } from "../user/userSlice";

const homogenizeArrays = (arrays: any[][]) => {
  let shortestArrLength: number = 1000;
  arrays.forEach((array) => {
    if (array.length < shortestArrLength) shortestArrLength = array.length;
  });
  const arrayMap = arrays.map((array) => {
    return array.splice(0, shortestArrLength);
  });
  return arrayMap;
};

const createHourString = (unixTime: number) => {
  let hourString: string = "";
  const hour = new Date(unixTime).getHours();
  if (hour < 12 && hour !== 0) {
    hourString = hour.toString() + " a.m.";
  } else if (hour === 0) {
    hourString = "12 a.m.";
  } else if (hour === 12) {
    hourString = "12 p.m.";
  } else {
    hourString = (hour - 12).toString() + " p.m.";
  }
  return hourString;
};

export const useMakeChartData = () => {
  const zoomFuncs = useZoom();
  return {
    handleDay: async (tokenData: TokenDataNew[]) => {
      let storeChartData = {} as ChartData;
      const symbols = tokenData.map((token) => token.symbol);
      let dayValues: number[] = [];
      let dayDates: string[] = [];
      let indexCount = 0;
      //12 returns 24 hr info
      //24 returns 12 hr info
      //48 returns 6 hr info
      const n = 24;
      await zoomFuncs.getDay(symbols).then((tokens) => {
        //not all arrays returned from CoinGecko are the same length
        //this function makes them all the same length
        tokens = homogenizeArrays(tokens);
        tokens.forEach((token: number[][], tokenIndex) => {
          for (let i = 0; i < token.length; i++) {
            if (i % n == 0 && dayValues[indexCount] === undefined) {
              dayValues[indexCount] = 0;
            }
            if (i % n == 0) {
              dayValues[indexCount] +=
                token[i][1] * tokenData[tokenIndex].balance;
              dayDates[indexCount] = createHourString(token[i][0]);
              indexCount++;
            }
          }
          //reset index count for next token evaluation
          indexCount = 0;
        });

        const dayChanges = dayValues.map((value) => {
          return Number((((value - dayValues[0]) / value) * 100).toFixed(2));
        });

        const dayPnlData = dayValues.map((value, index) => {
          if (index > 0) {
            return value - dayValues[index - 1];
          } else {
            return 0;
          }
        });

        let accumValue = 0;
        const dayCumulativePnlData = dayValues.map((quote, index) => {
          if (index > 0) {
            accumValue += quote - dayValues[index - 1];
            return accumValue;
          } else {
            return 0;
          }
        });

        storeChartData.dayAccumPnl = dayCumulativePnlData;
        storeChartData.dayValues = dayValues;
        storeChartData.dayChanges = dayChanges;
        storeChartData.dayDates = dayDates;
        storeChartData.dayPnl = dayPnlData;
      });
      return storeChartData;
    },

    handleWeek: (filteredList: any[], storeChartData: ChartData) => {
      //gets all the token values for 1 week and adds them up
      //7 for 1 week here, can change this to 1 month
      let dates: string[] = [];
      let weekQuoteData: number[] = [0, 0, 0, 0, 0, 0, 0];
      //accumulates the values of all tokens held for each day
      filteredList.forEach((token) => {
        for (let i = 0; i < 7; i++) {
          weekQuoteData[i] = weekQuoteData[i] + token.holdings[i].close.quote;
        }
      });

      const weekData = filteredList[0].holdings.splice(0, 7);
      weekData.forEach((date: any) => {
        let dateObj = new Date(date.timestamp);
        let mm = dateObj.getMonth() + 1;
        let dd = dateObj.getDate();
        dates.push(mm + "/" + dd);
      });

      //sets data to read from left to right
      weekQuoteData = weekQuoteData.reverse();
      dates = dates.reverse();

      //grabs all the changes for given dates
      const weekChanges = weekQuoteData.map((quote) => {
        return Number((((quote - weekQuoteData[0]) / quote) * 100).toFixed(2));
      });

      const weekPnlData = weekQuoteData.map((quote, index) => {
        if (index > 0) {
          return quote - weekQuoteData[index - 1];
        } else {
          return 0;
        }
      });

      let accumValue = 0;
      const weekCumulativePnlData = weekQuoteData.map((quote, index) => {
        if (index > 0) {
          accumValue = accumValue + (quote - weekQuoteData[index - 1]);
          return accumValue;
        } else {
          return 0;
        }
      });

      storeChartData.weekAccumPnl = weekCumulativePnlData;
      storeChartData.weekValues = weekQuoteData;
      storeChartData.weekDates = dates;
      storeChartData.weekPnl = weekPnlData;
      storeChartData.weekChanges = weekChanges;

      return storeChartData;
    },

    handleMonth: (filteredList: any[], storeChartData: ChartData) => {
      let dates: string[] = [];
      let monthQuoteData: number[] = [];
      let shortestLength = 1000;
      console.log(filteredList);
      //finds the length of the shortest array because they are sometimes different lengths
      filteredList.forEach((token) => {
        if (token.holdings.length < shortestLength)
          shortestLength = token.holdings.length;
      });
      //accumulates the values of all tokens held for each day
      filteredList.forEach((token) => {
        //homogenizes the lengths of all the arrays of holdings
        let holdings = token.holdings; //.splice(0, shortestLength);
        console.log(holdings.length);
        for (let i = 0; i < holdings.length; i++) {
          if (monthQuoteData[i] === undefined) monthQuoteData[i] = 0;
          monthQuoteData[i] += holdings[i].close.quote;
        }
      });

      //sets dates from covalent data and modifies it for readability
      const monthData = filteredList[1].holdings;
      monthData.forEach((date: any) => {
        let dateObj = new Date(date.timestamp);
        let mm = dateObj.getMonth() + 1;
        let dd = dateObj.getDate();
        dates.push(mm + "/" + dd);
      });

      //sets data to read from left to right
      monthQuoteData = monthQuoteData.reverse();
      dates = dates.reverse();

      //grabs all the changes for given dates
      const monthChanges = monthQuoteData.map((quote) => {
        return Number((((quote - monthQuoteData[0]) / quote) * 100).toFixed(2));
      });

      const monthPnlData = monthQuoteData.map((quote, index) => {
        if (index > 0) {
          return quote - monthQuoteData[index - 1];
        } else {
          return 0;
        }
      });

      let accumValue = 0;
      const monthCumulativePnlData = monthQuoteData.map((quote, index) => {
        if (index > 0) {
          accumValue += quote - monthQuoteData[index - 1];
          return accumValue;
        } else {
          return 0;
        }
      });

      storeChartData.monthAccumPnl = monthCumulativePnlData;
      storeChartData.monthValues = monthQuoteData;
      storeChartData.monthDates = dates;
      storeChartData.monthPnl = monthPnlData;
      storeChartData.monthChanges = monthChanges;

      return storeChartData;
    },
  };
};
